import { useEffect, useRef, useState } from 'react';
import { Truck } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Branch = { id: number; name: string };
type Transfer = { id: number; status: string; notes?: string; createdAt: string; fromBranch?: string; toBranch?: string; total: number; received: number };
type Item = { id: number; shipmentId: number; trackingNumber?: string; scannedOutAt?: string; receivedAt?: string; isUnexpected: boolean; receiverName?: string; receiverCity?: string; statusName?: string };
type Detail = { transfer: Transfer & { fromBranchId: number; toBranchId: number }; items: Item[] };

const statusLabel: Record<string, string> = { Open: 'مفتوح', InTransit: 'في الطريق', Closed: 'مغلق' };
const err = (e: any, fallback: string) => e?.response?.data?.message || fallback;

export default function BranchTransfersPage() {
  const scanRef = useRef<HTMLInputElement>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [from, setFrom] = useState(''); const [to, setTo] = useState(''); const [notes, setNotes] = useState('');
  const [detail, setDetail] = useState<Detail | null>(null);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    try { const r = await api.get('/api/branch-transfers'); setTransfers(r.data?.data || []); }
    catch (e) { setMessage(err(e, 'تعذر تحميل التحويلات.')); }
  };
  const open = async (id: number) => {
    try { const r = await api.get(`/api/branch-transfers/${id}`); setDetail(r.data?.data || null); setMessage(''); setTimeout(() => scanRef.current?.focus(), 50); }
    catch (e) { setMessage(err(e, 'تعذر فتح التحويل.')); }
  };
  useEffect(() => {
    void loadList();
    api.get('/api/branch-transfers/branches').then(r => setBranches(r.data?.data || [])).catch(() => setMessage('تعذر تحميل الفروع.'));
  }, []);

  const create = async () => {
    if (!from || !to || from === to) { setMessage('اختر فرعين مختلفين.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/branch-transfers', { fromBranchId: Number(from), toBranchId: Number(to), notes });
      setNotes(''); await loadList(); await open(r.data?.data?.id ?? r.data?.data?.Id);
    } catch (e) { setMessage(err(e, 'تعذر إنشاء التحويل.')); } finally { setBusy(false); }
  };

  const scan = async () => {
    if (!detail) return;
    const codes = code.split(/[\s,;]+/).map(c => c.trim()).filter(Boolean);
    setCode('');
    if (codes.length === 0) return;
    const path = detail.transfer.status === 'Open' ? 'scan-out' : 'scan-in';
    const notes: string[] = [];
    for (const c of codes) {
      try {
        const r = await api.post(`/api/branch-transfers/${detail.transfer.id}/${path}`, { code: c });
        if (r.data?.unexpected) notes.push(`${c}: استُلمت لكنها لم تكن ضمن قائمة الإرسال`);
      } catch (e) { notes.push(`${c}: ${err(e, 'فشل')}`); }
    }
    setMessage(notes.join(' — '));
    await open(detail.transfer.id); await loadList();
    scanRef.current?.focus();
  };

  const act = async (what: 'send' | 'close') => {
    if (!detail) return;
    const missing = detail.items.filter(i => !i.receivedAt).length;
    const q = what === 'send' ? `إرسال ${detail.items.length} شحنة؟` : (missing > 0 ? `يوجد ${missing} شحنة لم تُستلم. إغلاق التحويل؟` : 'إغلاق التحويل؟');
    if (!window.confirm(q)) return;
    setBusy(true);
    try {
      await api.post(`/api/branch-transfers/${detail.transfer.id}/${what}`);
      await open(detail.transfer.id); await loadList(); setMessage(what === 'send' ? 'تم الإرسال.' : 'تم إغلاق التحويل.');
    } catch (e) { setMessage(err(e, 'فشلت العملية.')); } finally { setBusy(false); }
  };

  const t = detail?.transfer;
  const receivedCount = detail?.items.filter(i => i.receivedAt).length ?? 0;

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Truck className="w-6 h-6" />التحويل بين الفروع</h1>
      {message && <p className="text-sm text-blue-700">{message}</p>}

      <Card>
        <CardHeader><CardTitle>تحويل جديد</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3">
          <select className="border rounded-md h-10 px-2" value={from} onChange={e => setFrom(e.target.value)}>
            <option value="">من فرع</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select className="border rounded-md h-10 px-2" value={to} onChange={e => setTo(e.target.value)}>
            <option value="">إلى فرع</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="ملاحظات (اختياري)" />
          <Button disabled={busy} onClick={() => void create()}>إنشاء</Button>
        </CardContent>
      </Card>

      {detail && t && (
        <Card>
          <CardHeader>
            <CardTitle>
              تحويل #{t.id} — {t.fromBranch} ← {t.toBranch}
              <span className="mr-3 text-sm font-normal text-gray-500">{statusLabel[t.status] || t.status}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {t.status !== 'Closed' && (
              <div className="flex gap-2">
                <Input ref={scanRef} value={code} onChange={e => setCode(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void scan(); } }}
                  placeholder={t.status === 'Open' ? 'امسح الشحنة للإرسال ثم Enter' : 'امسح الشحنة عند الاستلام ثم Enter'} />
                <Button variant="outline" onClick={() => void scan()}>إضافة</Button>
                {t.status === 'Open' && <Button disabled={busy || detail.items.length === 0} onClick={() => void act('send')}>إرسال ({detail.items.length})</Button>}
                {t.status === 'InTransit' && <Button disabled={busy} onClick={() => void act('close')}>إغلاق التحويل</Button>}
              </div>
            )}
            <p className="text-sm text-gray-600">الإجمالي: {detail.items.length} — المستلم: {receivedCount} — الناقص: {detail.items.filter(i => !i.receivedAt).length}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead><tr className="bg-gray-100 text-right">
                  <th className="p-2 border">رقم التتبع</th><th className="p-2 border">المستلم</th><th className="p-2 border">المدينة</th>
                  <th className="p-2 border">الحالة</th><th className="p-2 border">الاستلام</th>
                </tr></thead>
                <tbody>
                  {detail.items.length === 0 && <tr><td className="p-4 border text-center text-gray-500" colSpan={5}>لم تُضف شحنات بعد</td></tr>}
                  {detail.items.map(i => (
                    <tr key={i.id} className={i.isUnexpected ? 'bg-amber-50' : ''}>
                      <td className="p-2 border font-mono">{i.trackingNumber}{i.isUnexpected && <span className="text-amber-700 text-xs mr-2">زيادة</span>}</td>
                      <td className="p-2 border">{i.receiverName}</td><td className="p-2 border">{i.receiverCity}</td>
                      <td className="p-2 border">{i.statusName}</td>
                      <td className="p-2 border">{i.receivedAt ? new Date(i.receivedAt).toLocaleString('ar-EG') : <span className="text-red-600">لم تُستلم</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>التحويلات الأخيرة</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-gray-100 text-right">
              <th className="p-2 border">#</th><th className="p-2 border">من</th><th className="p-2 border">إلى</th>
              <th className="p-2 border">الحالة</th><th className="p-2 border">استُلم / الإجمالي</th><th className="p-2 border">التاريخ</th><th className="p-2 border"></th>
            </tr></thead>
            <tbody>
              {transfers.length === 0 && <tr><td className="p-4 border text-center text-gray-500" colSpan={7}>لا توجد تحويلات</td></tr>}
              {transfers.map(x => (
                <tr key={x.id}>
                  <td className="p-2 border">{x.id}</td><td className="p-2 border">{x.fromBranch}</td><td className="p-2 border">{x.toBranch}</td>
                  <td className="p-2 border">{statusLabel[x.status] || x.status}</td>
                  <td className="p-2 border">{x.received} / {x.total}</td>
                  <td className="p-2 border">{new Date(x.createdAt).toLocaleDateString('ar-EG')}</td>
                  <td className="p-2 border"><Button size="sm" variant="outline" onClick={() => void open(x.id)}>فتح</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
